import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, CreateUserRequest } from '../types/index';
import apiService from '../services/api';
import PageHeader from './ui/PageHeader';
import Badge from './ui/Badge';
import Button from './ui/Button';
import Modal from './ui/Modal';
import EmptyState from './ui/EmptyState';
import { PlusIcon, ExclamationCircleIcon, UsersIcon } from '@heroicons/react/24/outline';

const Residents: React.FC = () => {
  const { user: _currentUser } = useAuth();
  const [residents, setResidents] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    wardNumber: '',
  });

  useEffect(() => {
    fetchResidents();
  }, []);

  const fetchResidents = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getUsers();
      if (response.success && response.data) {
        // Filter only residents
        const residentsOnly = response.data.filter(user => user.role === 'resident');
        setResidents(residentsOnly);
      } else {
        throw new Error(response.error?.message || 'Failed to fetch residents');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch residents');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateResident = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const createUserData: CreateUserRequest = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: 'resident',
        departmentId: undefined, // Residents don't have departments
      };

      const response = await apiService.createUser(createUserData);
      
      if (response.success && response.data) {
        setShowCreateModal(false);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          wardNumber: '',
        });
        fetchResidents(); // Refresh the list
      } else {
        throw new Error(response.error?.message || 'Failed to create resident');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create resident');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDeleteResident = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this resident?')) return;
    
    try {
      const response = await apiService.deleteUser(id);
      if (response.success) {
        setResidents(prev => prev.filter(resident => resident.id !== id));
      } else {
        throw new Error(response.error?.message || 'Failed to delete resident');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete resident');
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const response = await apiService.updateUser(id, { isActive: !currentStatus });
      if (response.success && response.data) {
        setResidents(prev => prev.map(resident => 
          resident.id === id ? { ...resident, isActive: !currentStatus } : resident
        ));
      } else {
        throw new Error(response.error?.message || 'Failed to update resident');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update resident');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Residents"
        subtitle={`${residents.length} citizen accounts and resident profiles`}
        actions={
          <Button variant="primary" icon={PlusIcon} onClick={() => setShowCreateModal(true)}>
            Add New Resident
          </Button>
        }
      />

      {error && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <ExclamationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="mt-0.5 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {residents.length === 0 ? (
        <EmptyState icon={UsersIcon} title="No residents yet" description="Resident accounts created from the citizen app will appear here." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Resident</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Joined</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white">
                {residents.map((resident) => (
                  <tr key={resident.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100">
                          <span className="text-xs font-semibold text-primary-800">
                            {resident.firstName.charAt(0)}
                            {resident.lastName.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {resident.firstName} {resident.lastName}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{resident.email}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{resident.phone}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">Ward {resident.wardNumber || 'N/A'}</td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <Badge tone={resident.isActive ? 'success' : 'danger'}>{resident.isActive ? 'Active' : 'Inactive'}</Badge>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{new Date(resident.createdAt).toLocaleDateString()}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant={resident.isActive ? 'danger-ghost' : 'secondary'} onClick={() => handleToggleActive(resident.id, resident.isActive)}>
                          {resident.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button size="sm" variant="danger-ghost" onClick={() => handleDeleteResident(resident.id)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Resident Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New Resident"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button type="submit" form="create-resident-form" variant="primary">
              Create Resident
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreateResident} id="create-resident-form" className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">First Name</label>
            <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required className="input-field mt-1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Last Name</label>
            <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required className="input-field mt-1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
            <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="input-field mt-1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
            <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className="input-field mt-1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Ward Number</label>
            <input type="text" name="wardNumber" value={formData.wardNumber} onChange={handleInputChange} className="input-field mt-1" />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Residents;






